import type { OwnedUnit, RelicItem, SaveData, Settings, Wallet } from '../core/types';
import { createWallet } from '../economy/WalletService';
import { units } from '../data/content';

export const SAVE_VERSION = 4;
const SAVE_KEY = 'astral-chronicle-save-v3';
const BACKUP_KEY = 'astral-chronicle-save-backup';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const defaultSettings = (): Settings => ({ masterVolume: 0.8, musicVolume: 0.55, sfxVolume: 0.8, muted: false, quality: 'medium', reduceMotion: false, reduceFlash: false, reduceParticles: false, largeText: false, highContrast: false, damageNumbers: true, outline: true, cinematicCamera: true });

const starterUnit = (definitionId: string, acquiredAt: number): OwnedUnit => ({
  instanceId: crypto.randomUUID(), definitionId, level: 1, exp: 0, ascension: 0, awakening: 0, evolved: false, shards: 0,
  trait: { id: 'fierce-starter', name: 'Fierce', tier: 'Normal', stat: 'attack', value: 0.04 }, locked: true, acquiredAt, relicSlots: {},
});

const starterRelics = (): RelicItem[] => [
  ['relic-inferno-weapon', 'Cinderbrand Memory', 'weapon', 'Inferno', 'Epic', 'attack'],
  ['relic-glacier-armor', 'Rimeglass Mantle', 'armor', 'Glacier', 'Rare', 'defense'],
  ['relic-vanguard-core', 'Unbroken Core', 'core', 'Vanguard', 'Legendary', 'hp'],
  ['relic-hunter-weapon', 'Farstar Edge', 'weapon', 'Hunter', 'Epic', 'critRate'],
  ['relic-oracle-core', 'Oracle Seed', 'core', 'Oracle', 'Epic', 'healingBonus'],
  ['relic-tempest-armor', 'Stormstep Mail', 'armor', 'Tempest', 'Rare', 'speed'],
].map(([id, name, slot, set, rarity, mainStat], index) => ({ id, name, slot, set, rarity, mainStat, level: 1, locked: false, value: 0.05 + index * 0.01 } as RelicItem));

export function createNewSave(now = Date.now()): SaveData {
  const starters = ['kael-cinder', 'mira-moss', 'bryn-iceward', 'volt-scout', 'orren-gale'].map((id, index) => starterUnit(id, now + index));
  return {
    version: SAVE_VERSION, id: crypto.randomUUID(), createdAt: now, updatedAt: now, playTimeSeconds: 0,
    playerName: 'Chronicle Keeper', playerLevel: 1, playerExp: 0, victories: 0, wallet: createWallet(), units: starters, relics: starterRelics(),
    teams: [starters.map((unit) => unit.instanceId), [], []], activeTeam: 0, unlockedStage: 1, stageStars: {}, claimedFirstRewards: [], towerFloor: 1,
    summon: { standardPity: 0, featuredPity: 0, featuredGuarantee: false, beginnerPulls: 0, history: [] }, quests: {}, claimedCodes: [], settings: defaultSettings(), tutorialComplete: false,
  };
}

export function migrateSave(input: unknown): SaveData {
  if (!input || typeof input !== 'object') throw new Error('Save is not an object');
  const raw = structuredClone(input) as Record<string, unknown>;
  let version = typeof raw.version === 'number' ? raw.version : 1;
  if (version > SAVE_VERSION) throw new Error('Save was created by a newer game version');
  if (version < 2) {
    raw.claimedFirstRewards = raw.claimedFirstRewards ?? [];
    raw.claimedCodes = raw.claimedCodes ?? [];
    version = 2;
  }
  if (version < 3) {
    raw.settings = { ...defaultSettings(), ...((raw.settings as Partial<Settings>) ?? {}) };
    raw.quests = raw.quests ?? {};
    version = 3;
  }
  if (version < 4) {
    raw.relics = raw.relics ?? starterRelics();
    const oldWallet = raw.wallet as Partial<Wallet> | undefined;
    if (oldWallet) oldWallet.evolutionMaterials ??= 0;
    const oldUnits = raw.units as Array<Partial<OwnedUnit>> | undefined;
    oldUnits?.forEach((unit) => { unit.evolved ??= false; unit.relicSlots ??= {}; });
    version = 4;
  }
  raw.version = version;
  return validateSave(raw);
}

export function validateSave(input: unknown): SaveData {
  if (!input || typeof input !== 'object') throw new Error('Save data is missing');
  const save = input as SaveData;
  if (save.version !== SAVE_VERSION) throw new Error(`Unsupported save version: ${save.version}`);
  if (typeof save.id !== 'string' || !save.id || !Array.isArray(save.units) || !Array.isArray(save.teams)) throw new Error('Save identity or roster is invalid');
  const currencyKeys: Array<keyof Wallet> = ['gold', 'gems', 'tickets', 'expCrystals', 'traitTokens', 'ascensionCrystals', 'evolutionMaterials', 'relicDust', 'raidTokens', 'eventCurrency', 'secretFragments'];
  for (const key of currencyKeys) if (!Number.isSafeInteger(save.wallet?.[key]) || save.wallet[key] < 0) throw new Error(`Invalid currency: ${key}`);
  const ids = new Set<string>();
  for (const owned of save.units) {
    if (ids.has(owned.instanceId)) throw new Error('Duplicate owned unit instance');
    ids.add(owned.instanceId);
    if (!units.some((unit) => unit.id === owned.definitionId)) throw new Error(`Unknown unit in save: ${owned.definitionId}`);
    if (!Number.isInteger(owned.level) || owned.level < 1 || owned.level > 100) throw new Error('Invalid unit level');
    owned.evolved = Boolean(owned.evolved); owned.relicSlots ??= {};
  }
  save.relics ??= starterRelics();
  save.teams = save.teams.slice(0, 8).map((team) => [...new Set(team.filter((id) => ids.has(id)))].slice(0, 5));
  save.unlockedStage = Math.min(70, Math.max(1, Math.floor(save.unlockedStage || 1)));
  save.settings = { ...defaultSettings(), ...save.settings };
  return save;
}

export class SaveManager {
  private data: SaveData;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly storage: StorageLike = localStorage) {
    this.data = this.load();
  }

  get current(): SaveData { return this.data; }

  load(): SaveData {
    const text = this.storage.getItem(SAVE_KEY);
    if (!text) return createNewSave();
    try { return migrateSave(JSON.parse(text) as unknown); }
    catch (error) {
      console.error('Primary save recovery failed', error);
      const backup = this.storage.getItem(BACKUP_KEY);
      if (backup) {
        try { return migrateSave(JSON.parse(backup) as unknown); }
        catch (backupError) { console.error('Backup save recovery failed', backupError); }
      }
      const fresh = createNewSave();
      this.storage.setItem('astral-chronicle-corrupt-save', text);
      return fresh;
    }
  }

  commit(): void {
    this.data.updatedAt = Date.now();
    const previous = this.storage.getItem(SAVE_KEY);
    if (previous) this.storage.setItem(BACKUP_KEY, previous);
    this.storage.setItem(SAVE_KEY, JSON.stringify(validateSave(structuredClone(this.data))));
  }

  scheduleCommit(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => { this.commit(); this.saveTimer = null; }, 180);
  }

  export(): string { return JSON.stringify(validateSave(structuredClone(this.data)), null, 2); }

  import(text: string): void { this.data = migrateSave(JSON.parse(text) as unknown); this.commit(); }

  reset(): void { this.storage.removeItem(SAVE_KEY); this.storage.removeItem(BACKUP_KEY); this.data = createNewSave(); this.commit(); }
}
